import type {
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  Office
} from "@gettactic/api";
import type { AuthenticatedUser } from "@gettactic/api";
import { useMemo } from "react";
import { QUICK_ACTIONS, SETTINGS } from "../constants";
import type { Setting } from "../constants";
import { searchOffices, searchSettings } from "../searchUtils";

interface UseSearchResultsParams {
  query: string;
  resources: GlobalSearchResourceItem[];
  people: GlobalSearchUserResult[];
  offices: Office[];
  hasNoApiResults: boolean;
  isAdmin: boolean;
  authenticatedUser: AuthenticatedUser;
}

export function useSearchResults({
  query,
  resources,
  people,
  offices,
  hasNoApiResults,
  isAdmin,
  authenticatedUser
}: UseSearchResultsParams) {
  const filteredSettings = useMemo((): Setting[] => {
    const results = searchSettings(
      SETTINGS.filter((s) => (s.isAdmin ? isAdmin : true)),
      query
    );
    return results.map((r) => r.item as Setting);
  }, [query, isAdmin]);

  const filteredActions = useMemo((): Setting[] => {
    const results = searchSettings(QUICK_ACTIONS, query);
    return results
      .map((r) => r.item as Setting)
      .filter((s) => (s.isAvailable ? s.isAvailable(authenticatedUser) : true));
  }, [
    query,

    // these two are needed for the isAvailable check
    // whole authenticatedUser is passed instead for easier typing
    authenticatedUser?.organization?.desk_bookings_enabled,
    authenticatedUser?.organization?.meeting_room_bookings_enabled
  ]);

  const filteredOffices = useMemo((): Office[] => {
    const results = searchOffices(offices, query);
    return results.map((r) => r.item) as Office[];
  }, [offices, query]);

  const hasNoResults = useMemo(() => {
    return (
      hasNoApiResults &&
      filteredActions.length === 0 &&
      filteredSettings.length === 0 &&
      resources.length === 0 &&
      people.length === 0 &&
      filteredOffices.length === 0
    );
  }, [
    hasNoApiResults,
    filteredActions.length,
    filteredSettings.length,
    resources.length,
    people.length,
    filteredOffices.length
  ]);

  const totalResults = useMemo(() => {
    return (
      filteredActions.length +
      filteredSettings.length +
      resources.length +
      people.length +
      filteredOffices.length
    );
  }, [
    filteredActions.length,
    filteredSettings.length,
    resources.length,
    people.length,
    filteredOffices.length
  ]);

  return {
    filteredSettings,
    filteredActions,
    filteredOffices,
    hasNoResults,
    totalResults
  };
}
